import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  AlertTriangle,
  ChevronLeft,
  Hourglass,
  Loader2,
  ArrowRight,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useLinkedSlots } from '@/hooks/useLinkedSlots';
import type { Service, CustomAnswers } from '@/types/booking';
import type { Organization } from '@/types/organization';
import type { NormalizedSlot } from '@/utils/normalizeSlot';
import type { UseFormReturn } from 'react-hook-form';
import type { CustomerFormData } from '../hooks/useBookingFlow';

interface CustomerDetailsProps {
  selectedService: Service | null;
  selectedSlot: NormalizedSlot | null;
  organization: Organization;
  customerForm: UseFormReturn<CustomerFormData>;
  onSubmit: (data: CustomerFormData) => void;
  customAnswers: CustomAnswers;
  setCustomAnswers: (answers: CustomAnswers) => void;
  isSavingBooking: boolean;
  onBack: () => void;
  // The booking steps put the submit button in the sidebar and the mobile
  // bar (both use form={DETAILS_FORM_ID}); the form's own Back/Confirm row
  // only shows on desktop when there is no sidebar.
  showActions?: boolean;
}

export const DETAILS_FORM_ID = 'booking-details-form';
const NOTES_MAX = 500;

export function CustomerDetails({
  selectedService,
  selectedSlot,
  organization,
  customerForm,
  onSubmit,
  customAnswers,
  setCustomAnswers,
  isSavingBooking,
  onBack,
  showActions = true,
}: CustomerDetailsProps) {
  const { t } = useTranslation(['publicBooking', 'bookingPage']);
  const questions = [...(selectedService?.custom_booking_questions || selectedService?.customBookingQuestions || [])]
    .sort((a, b) => a.order - b.order);
  const orgName = organization?.name || organization?.business_name;

  const isWaitlistMode = !!(selectedSlot?.isFull && selectedSlot?.waitlistEnabled);

  // Linked slots — fetched only for legacy numeric-id slots. Availability-based
  // slots use string IDs and there's no linked-slot endpoint for them yet.
  const linkedSlotId =
    selectedSlot && typeof selectedSlot.id === 'number' ? selectedSlot.id : null;
  const { linkedSlots, linkedCount } = useLinkedSlots(linkedSlotId);

  return (
    <div className="flex flex-col gap-5">
      {isWaitlistMode && (
        <div className="flex items-start gap-3 rounded-2xl bg-brand-50 px-4 py-3.5 text-brand-700" data-testid="waitlist-note">
          <Hourglass className="mt-0.5 h-[18px] w-[18px] shrink-0" />
          <div className="flex flex-col gap-1">
            <span className="text-[15px] font-semibold">{t('bookingPage:steps.waitlistNote.title')}</span>
            <span className="text-[14px] leading-normal">{t('bookingPage:steps.waitlistNote.body')}</span>
          </div>
        </div>
      )}

      {linkedCount > 0 && (
        <Alert variant="destructive" className="border-warn/40 bg-warn-soft text-warn-text">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t('publicBooking:confirmation.blockedServicesTitle')}</AlertTitle>
          <AlertDescription>
            <p className="mb-2">
              {t('publicBooking:confirmation.blockedServicesMessage')}
            </p>
            <ul className="space-y-1">
              {linkedSlots.map((slot) => {
                const start = (slot as any).startTime || (slot as any).start_time;
                const end = (slot as any).endTime || (slot as any).end_time;
                const service = slot.service as { name?: string; duration_minutes?: number; duration?: number } | undefined;
                return (
                  <li key={slot.id} className="flex items-center gap-2 text-body-sm">
                    <XCircle className="h-4 w-4 shrink-0" />
                    <span>
                      {service?.name} ({service?.duration_minutes ?? service?.duration} min)
                      {' — '}
                      {start && format(new Date(start), 'HH:mm')}
                      {' – '}
                      {end && format(new Date(end), 'HH:mm')}
                    </span>
                  </li>
                );
              })}
            </ul>
          </AlertDescription>
        </Alert>
      )}

        <Form {...customerForm}>
          <form
            id={DETAILS_FORM_ID}
            onSubmit={customerForm.handleSubmit(onSubmit)}
            className="flex flex-col gap-4 rounded-[18px] border border-line bg-surface p-[18px] sm:p-6"
            noValidate
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={customerForm.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('publicBooking:form.firstName')} {t('publicBooking:form.required')}
                    </FormLabel>
                    <FormControl>
                      <Input placeholder={t('publicBooking:form.firstNamePlaceholder')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={customerForm.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('publicBooking:form.lastName')} {t('publicBooking:form.required')}
                    </FormLabel>
                    <FormControl>
                      <Input placeholder={t('publicBooking:form.lastNamePlaceholder')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={customerForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('publicBooking:form.email')} {t('publicBooking:form.required')}
                  </FormLabel>
                  <FormControl>
                    <Input type="email" placeholder={t('publicBooking:form.emailPlaceholder')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={customerForm.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('publicBooking:form.phone')}
                    {organization?.booking_settings?.require_phone && (
                      <span className="text-red-500 ml-1">{t('publicBooking:form.required')}</span>
                    )}
                  </FormLabel>
                  <FormControl>
                    <PhoneInput
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t('publicBooking:form.phonePlaceholder')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={customerForm.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('publicBooking:form.notes')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('publicBooking:form.notesPlaceholder')}
                      rows={3}
                      maxLength={NOTES_MAX}
                      {...field}
                    />
                  </FormControl>
                  <span className="flex justify-end text-[12px] tabular-nums text-soft" data-testid="notes-counter">
                    {(field.value || '').length} / {NOTES_MAX}
                  </span>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Custom Questions */}
            {questions.length > 0 && orgName && (
              <span className="pt-1.5 text-[15px] font-semibold text-ink">
                {t('bookingPage:steps.questionsFrom', { name: orgName })}
              </span>
            )}
            {questions.map((question) => (
                <div key={question.id} className="space-y-2">
                  <Label htmlFor={`custom-${question.id}`}>
                    {question.question}
                    {question.required && <span className="text-red-500 ml-1">*</span>}
                  </Label>

                  {question.type === 'text' && (
                    <Input
                      id={`custom-${question.id}`}
                      value={(customAnswers[question.id] as string) || ''}
                      onChange={(e) => setCustomAnswers({ ...customAnswers, [question.id]: e.target.value })}
                      required={question.required}
                    />
                  )}

                  {question.type === 'select' && (
                    <Select
                      value={(customAnswers[question.id] as string) || ''}
                      onValueChange={(value) => setCustomAnswers({ ...customAnswers, [question.id]: value })}
                    >
                      <SelectTrigger id={`custom-${question.id}`}>
                        <SelectValue placeholder={t('publicBooking:customQuestions.selectPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {question.options?.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {question.type === 'checkbox' && (
                    <div className="space-y-2">
                      {question.options?.map((option) => (
                        <div key={option} className="flex items-center space-x-2">
                          <Checkbox
                            id={`custom-${question.id}-${option}`}
                            checked={((customAnswers[question.id] as string[]) || []).includes(option)}
                            onCheckedChange={(checked) => {
                              const currentAnswers = (customAnswers[question.id] as string[]) || [];
                              const newAnswers = checked
                                ? [...currentAnswers, option]
                                : currentAnswers.filter((a) => a !== option);
                              setCustomAnswers({ ...customAnswers, [question.id]: newAnswers });
                            }}
                          />
                          <label
                            htmlFor={`custom-${question.id}-${option}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            {option}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

            <p className="text-[13px] leading-normal text-soft">
              {t('bookingPage:steps.privacy', { name: orgName })}
            </p>

            <div className={showActions ? 'hidden justify-between lg:flex' : 'hidden'}>
              <Button
                type="button"
                variant="outline"
                onClick={onBack}
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                {t('publicBooking:navigation.back')}
              </Button>
              <Button
                type="submit"
                disabled={isSavingBooking}
              >
                {isSavingBooking ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {isWaitlistMode
                      ? t('publicBooking:navigation.joiningWaitlist', 'Joining waitlist...')
                      : t('publicBooking:navigation.booking')}
                  </>
                ) : (
                  <>
                    {isWaitlistMode
                      ? t('publicBooking:navigation.joinWaitlist', 'Join waitlist')
                      : t('publicBooking:navigation.confirmBooking')}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
    </div>
  );
}
